import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useServices } from '@/context/ServiceContext'
import { useAuth } from '@/context/AuthContext'
import { EpisodeThumbnail } from '@/components/EpisodeThumbnail'
import { DifficultyBadge } from '@/components/DifficultyBadge'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { AskSarahButton } from '@/components/sarah/AskSarahButton'
import type { Episode } from '@/domain/episode'
import type { EpisodeProgress } from '@/domain/progress'
import type { QuizResult } from '@/domain/result'

function formatDuration(seconds: number) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m} min ${s > 0 ? `${s}s` : ''}`.trim()
}

export function EpisodeDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const { episodes, progress, quiz, results } = useServices()
  const { currentUser } = useAuth()
  const navigate = useNavigate()

  const [episode, setEpisode] = useState<Episode | null>(null)
  const [episodeProgress, setEpisodeProgress] = useState<EpisodeProgress | null>(null)
  const [hasQuiz, setHasQuiz] = useState(false)
  const [quizResults, setQuizResults] = useState<QuizResult[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!id || !currentUser) return
    setLoading(true)
    Promise.all([
      episodes.getById(id),
      progress.getForEpisode(currentUser.id, id),
      quiz.getQuestionsForEpisode(id),
      results.listForEpisode(currentUser.id, id),
    ]).then(([ep, prog, questions, res]) => {
      if (!ep) {
        setNotFound(true)
      } else {
        setEpisode(ep)
        setEpisodeProgress(prog)
        setHasQuiz(questions.length > 0)
        setQuizResults(res)
      }
      setLoading(false)
    })
  }, [id, currentUser, episodes, progress, quiz, results])

  if (loading) return <LoadingSpinner label="Loading episode…" />
  if (notFound || !episode) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-white">Episode not found</h1>
        <Link to="/episodes" className="cyber-btn-secondary mt-6 inline-flex">
          ← Back to Library
        </Link>
      </div>
    )
  }

  const isCompleted = episodeProgress?.completed ?? false
  const hasStarted = !!episodeProgress
  const hasPassed = quizResults.some((r) => r.passed)
  const bestScore = quizResults.reduce((max, r) => Math.max(max, r.scorePercent), 0)
  const latestResult = quizResults[quizResults.length - 1]

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
      <Link to="/episodes" className="text-sm text-white/50 hover:text-white">
        ← Episode Library
      </Link>

      <div className="mt-4 overflow-hidden rounded-2xl">
        <EpisodeThumbnail gradient={episode.thumbnailGradient} title={episode.title} className="h-56 w-full sm:h-72" />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <DifficultyBadge difficulty={episode.difficulty} />
        <span className="cyber-pill bg-white/5 text-white/60 ring-1 ring-white/10">
          {formatDuration(episode.durationSeconds)}
        </span>
        {isCompleted && (
          <span className="cyber-pill bg-teal-500/15 text-teal-300 ring-1 ring-teal-500/30">✓ Watched</span>
        )}
        {hasPassed && (
          <span className="cyber-pill bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30">
            🏅 Quiz Passed — {bestScore}%
          </span>
        )}
      </div>

      <h1 className="mt-4 font-display text-3xl font-bold text-white">{episode.title}</h1>
      <p className="mt-3 text-white/70">{episode.synopsis}</p>

      <div className="mt-8 cyber-panel p-6">
        <h2 className="font-display font-semibold text-white">What you'll learn</h2>
        <ul className="mt-3 space-y-2">
          {episode.learningObjectives.map((obj) => (
            <li key={obj} className="flex items-start gap-2 text-sm text-white/70">
              <span className="mt-1 text-teal-400">✓</span>
              <span>{obj}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <button onClick={() => navigate(`/episodes/${episode.id}/watch`)} className="cyber-btn-primary">
          {hasStarted && !isCompleted ? 'Resume Episode' : isCompleted ? 'Watch Again' : 'Watch Episode'}
        </button>

        {hasQuiz ? (
          isCompleted ? (
            <button onClick={() => navigate(`/episodes/${episode.id}/quiz`)} className="cyber-btn-secondary">
              {hasPassed ? 'Retake Quiz' : quizResults.length > 0 ? 'Try the Quiz Again' : 'Take the Quiz'}
            </button>
          ) : (
            <button
              disabled
              title="Watch the full episode to unlock its quiz"
              className="cyber-btn-secondary cursor-not-allowed opacity-50"
            >
              Take the Quiz — watch episode first
            </button>
          )
        ) : (
          <button disabled className="cyber-btn-secondary cursor-not-allowed opacity-50">
            No quiz for this episode yet
          </button>
        )}

        {latestResult && (
          <Link
            to={`/episodes/${episode.id}/quiz/results/${latestResult.id}`}
            className="cyber-btn-secondary"
          >
            View Last Result
          </Link>
        )}
      </div>

      {isCompleted && (
        <div className="mt-8 max-w-md">
          <AskSarahButton
            variant="card"
            label="Want to practice this topic with Sarah?"
            episodeId={episode.id}
            initialPrompt={`Give me a scenario about "${episode.title}"`}
          />
        </div>
      )}
    </div>
  )
}
